import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActions, Typography } from '@mui/material';
import ConfrereButton from '../ConfrereButton';
import ErrorTitle from './ErrorTitle';
import {
  setVideoError,
  setMicrophoneError,
  setSpeakerError,
  setNetworkError,
  pushEncounteredError,
  setIgnorable,
  clearEncounteredErrors,
} from '../../store/slices/statusSlice';
import {
  CameraTakenError,
  VideoPermissionBlockedError,
  VideoPermissionDeniedError,
  NoCameraError,
} from './Video';
import {
  MicrophonePermissionBlockedError,
  MicrophonePermissionDeniedError,
  NoMicrophoneError,
} from './Microphone';
import {
  NetworkSocketError,
  NetworkStreamError,
  NetworkStreamingError,
  NetworkTokenError,
  NetworkUnexpectedError,
} from './Network';
import { NoSpeakerError } from './Speaker';
import { clearScenario } from '../../store/slices/scenarioSlice';

const errorComponentMap = {
  noCamera: NoCameraError,
  cameraTaken: CameraTakenError,
  cameraBlocked: VideoPermissionBlockedError,
  cameraDismissed: VideoPermissionDeniedError,
  noMicrophone: NoMicrophoneError,
  microphoneDismissed: MicrophonePermissionDeniedError,
  microphoneBlocked: MicrophonePermissionBlockedError,
  noSpeaker: NoSpeakerError,
  streamError: NetworkStreamError,
  streamingError: NetworkStreamingError,
  tokenError: NetworkTokenError,
  socketError: NetworkSocketError,
  unexpectedError: NetworkUnexpectedError,
};

function ErrorDialog({ open, reloadingRef }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { video, microphone, speaker, network, encountered } = useSelector(
    (state) => state.status
  );
  const { onCompletion } = useSelector((state) => state.scenario);
  const [errorEncountered, setErrorEncountered] = useState(false);

  let ActiveErrorComponent = null;
  let activeErrorType = null;
  let activeErrorMessage = null;

  if (video) {
    ActiveErrorComponent = errorComponentMap[video];
    activeErrorType = 'video';
    activeErrorMessage = video;
  } else if (microphone) {
    ActiveErrorComponent = errorComponentMap[microphone];
    activeErrorType = 'microphone';
    activeErrorMessage = microphone;
  } else if (speaker) {
    ActiveErrorComponent = errorComponentMap[speaker];
    activeErrorType = 'speaker';
    activeErrorMessage = speaker;
  } else if (network) {
    ActiveErrorComponent = errorComponentMap[network];
    activeErrorType = 'network';
    activeErrorMessage = network;
  }

  const handleIgnoreClick = () => {
    switch (activeErrorType) {
      case 'video':
        dispatch(setVideoError(''));
        break;
      case 'microphone':
        dispatch(setMicrophoneError(''));
        break;
      case 'speaker':
        dispatch(setSpeakerError(''));
        break;
      case 'network':
        dispatch(clearEncounteredErrors());
        dispatch(clearScenario());
        onCompletion();
        dispatch(setNetworkError(''));
        break;
      default:
        break;
    }
  };

  const reload = () => {
    reloadingRef.current = true;
    dispatch(
      pushEncounteredError({
        type: activeErrorType,
        message: activeErrorMessage,
      })
    );
    window.location.reload();
  };

  useEffect(() => {
    if (!activeErrorType || !activeErrorMessage) return;

    const encounteredError = encountered.some(
      (error) =>
        error.type === activeErrorType && error.message === activeErrorMessage
    );
    dispatch(setIgnorable(encounteredError));
    setErrorEncountered(encounteredError);
  }, [activeErrorType, activeErrorMessage, encountered, dispatch]);

  if (reloadingRef.current) return null;

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth="xs"
      sx={{ textAlign: 'center' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="error-dialog-title"
    >
      {ActiveErrorComponent ? (
        <ActiveErrorComponent
          errorType={activeErrorType}
          errorMessage={activeErrorMessage}
        />
      ) : (
        <ErrorTitle title={t('Error')} />
      )}
      <DialogActions
        sx={{
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <ConfrereButton
          onClick={reload}
          sx={{ margin: '8px 16px' }}
          size="small"
        >
          <Typography sx={{ fontSize: '14px' }}>{t('Retry')}</Typography>
        </ConfrereButton>
        {errorEncountered && (
          <ConfrereButton
            onClick={handleIgnoreClick}
            outlined={true}
            sx={{ margin: '8px 16px' }}
            size="small"
          >
            <Typography sx={{ fontSize: '14px' }}>{t('Ignore')}</Typography>
          </ConfrereButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ErrorDialog;
